import { useEffect, useRef, useState } from 'react';
import { Button, Spinner } from '@nextui-org/react';
import { Select, SelectItem } from '@nextui-org/react';
import { toast } from 'react-toastify';
import useUserStore from '../../../../common/src/stores/useUserStore';
import useReceiverStore from '../../store/useReceiverStore';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';

export const ReceiverMappingSection = ({
  speciesId,
  season,
  hide,
  maxColumns = 3,
}: {
  speciesId: number;
  season: string;
  hide: boolean;
  maxColumns?: 2 | 3;
}) => {
  const { t } = useTranslation('settings');
  const { userProfile, session } = useUserStore();
  const {
    mappedReceivers,
    directMappedReceivers,
    unmappedReceivers,
    fetchReceiverMappings,
    saveMapping,
    deleteMapping,
    mappingLoading,
    exporterReceivers,
    fetchExporterReceivers,
    exporterReceiversLoading,
  } = useReceiverStore();
  const [isOpen, setIsOpen] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const mutationVersion = useRef(0);
  const pendingMutation = useRef(false);

  useEffect(() => {
    mutationVersion.current += 1;
    pendingMutation.current = false;
    setIsSaving(false);
    return () => {
      mutationVersion.current += 1;
    };
  }, [speciesId, season, userProfile?.exporter_id]);

  useEffect(() => {
    if (userProfile?.exporter_id) {
      fetchExporterReceivers(userProfile.exporter_id).catch(() => null);
    }
  }, [userProfile?.exporter_id, fetchExporterReceivers]);

  useEffect(() => {
    if (userProfile && session && !hide) {
      fetchReceiverMappings(speciesId, season, session, userProfile);
    }
  }, [speciesId, season, session, userProfile, hide, fetchReceiverMappings]);

  if (hide) return null;

  const isLoading = mappingLoading || exporterReceiversLoading;
  const displayedMappedReceivers = [
    ...mappedReceivers,
    ...directMappedReceivers,
  ];
  const mappingContext =
    session && userProfile
      ? { speciesId, season, session, userProfile }
      : undefined;
  const mappingGridClassName =
    maxColumns === 2
      ? 'grid grid-cols-1 gap-4 md:grid-cols-2'
      : 'grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3';

  const handleSaveMapping = async (
    receiverName: string,
    exporterReceiverId: string
  ) => {
    const selectedReceiverId = Number(exporterReceiverId);
    if (
      !userProfile?.exporter_id ||
      !selectedReceiverId ||
      pendingMutation.current
    )
      return;
    const version = ++mutationVersion.current;
    pendingMutation.current = true;
    setIsSaving(true);
    try {
      await saveMapping(
        receiverName,
        selectedReceiverId,
        userProfile.exporter_id,
        mappingContext
      );
    } catch {
      if (version === mutationVersion.current)
        toast.error(
          t('No se pudo guardar el mapeo de clientes. Inténtalo nuevamente.')
        );
    } finally {
      if (version === mutationVersion.current) {
        pendingMutation.current = false;
        setIsSaving(false);
      }
    }
  };

  const handleDeleteMapping = async (
    mappingId: number,
    receiverName: string
  ) => {
    if (!userProfile?.exporter_id || pendingMutation.current) return;
    const version = ++mutationVersion.current;
    pendingMutation.current = true;
    setIsSaving(true);
    try {
      await deleteMapping(
        mappingId,
        receiverName,
        userProfile.exporter_id,
        mappingContext
      );
    } catch {
      if (version === mutationVersion.current)
        toast.error(
          t('No se pudo eliminar el mapeo de clientes. Inténtalo nuevamente.')
        );
    } finally {
      if (version === mutationVersion.current) {
        pendingMutation.current = false;
        setIsSaving(false);
      }
    }
  };

  return isLoading ? (
    <div className='flex justify-center items-center h-full'>
      <Spinner />
    </div>
  ) : (
    <div className='mb-4'>
      <div className='mb-4'>
        <div className='flex items-center justify-between '>
          <div className='flex items-center gap-2'>
            <p className='text-md font-bold text-default-600'>
              {t('Mapeo de Clientes')}
            </p>
            {unmappedReceivers.length > 0 ? (
              <div className='flex items-center gap-2'>
                <span className='inline-flex items-center rounded-full bg-yellow-100 dark:bg-yellow-900 px-2.5 py-0.5 text-xs font-medium text-yellow-800 dark:text-yellow-200'>
                  {unmappedReceivers.length} {t('sin mapear')}
                </span>
                <span className='inline-flex items-center rounded-full bg-green-100 dark:bg-green-900 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:text-green-200'>
                  {displayedMappedReceivers.length} {t('mapeados')}
                </span>
              </div>
            ) : (
              <span className='inline-flex items-center rounded-full bg-green-100 dark:bg-green-900 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:text-green-200'>
                {t('Todos mapeados')}
              </span>
            )}
          </div>
          <Button
            size='sm'
            variant='light'
            onClick={() => setIsOpen(!isOpen)}
            className='min-w-[100px] px-4 py-2 text-sm font-medium text-primary-500'
          >
            {isOpen ? t('Ocultar Mapeo') : t('Mostrar Mapeo')}
          </Button>
        </div>
        <p className='text-xs text-default-400'>
          {t(
            'Indica el cliente "estandarizado" que corresponde a cada cliente presente en la Base de Despachos'
          )}
        </p>
      </div>

      <div></div>

      {isOpen && (
        <>
          {displayedMappedReceivers.length > 0 && (
            <div className='mb-4'>
              <div className={mappingGridClassName}>
                {displayedMappedReceivers.map((mapping) => (
                  <div
                    key={`${mapping.is_direct_match ? 'direct' : 'mapping'}-${mapping.id}`}
                    className='grid grid-cols-1 items-center gap-2 rounded-md border border-default-200 bg-default-100 p-3 sm:grid-cols-[minmax(0,1fr)_minmax(12rem,1.2fr)_auto] dark:border-default-100'
                  >
                    <span className='min-w-0 break-words text-sm font-medium'>
                      {mapping.frutic_receiver_name}:
                    </span>
                    <div className='min-w-0'>
                      <Select
                        size='sm'
                        isDisabled={isSaving}
                        className='w-full'
                        selectedKeys={[mapping.exporter_receiver_id.toString()]}
                        onChange={(e) =>
                          handleSaveMapping(
                            mapping.frutic_receiver_name,
                            e.target.value
                          )
                        }
                      >
                        {exporterReceivers.map((receiver) => (
                          <SelectItem
                            key={receiver.id.toString()}
                            value={receiver.id.toString()}
                          >
                            {receiver.name}
                          </SelectItem>
                        ))}
                      </Select>
                    </div>
                    <button
                      disabled={isSaving}
                      aria-label={t('Eliminar mapeo')}
                      onClick={() =>
                        handleDeleteMapping(
                          mapping.id!,
                          mapping.frutic_receiver_name
                        )
                      }
                      className='justify-self-end p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300'
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        className='h-5 w-5'
                        viewBox='0 0 20 20'
                        fill='currentColor'
                      >
                        <path
                          fillRule='evenodd'
                          d='M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z'
                          clipRule='evenodd'
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {unmappedReceivers.length > 0 && (
            <div className=''>
              <div className={mappingGridClassName}>
                {unmappedReceivers.map((receiverName) => (
                  <div
                    key={receiverName}
                    className='grid grid-cols-1 items-center gap-2 rounded-md border border-yellow-300 bg-yellow-50 p-3 sm:grid-cols-[minmax(0,1fr)_minmax(12rem,1.2fr)] dark:border-yellow-700 dark:bg-yellow-950'
                  >
                    <span className='min-w-0 break-words text-sm font-medium'>
                      {receiverName}:
                    </span>
                    <div className='min-w-0'>
                      <Select
                        size='sm'
                        isDisabled={isSaving}
                        className='w-full'
                        placeholder={t('Seleccionar cliente')}
                        onChange={(e) => {
                          handleSaveMapping(receiverName, e.target.value);
                        }}
                      >
                        {exporterReceivers.map((receiver) => (
                          <SelectItem
                            key={receiver.id.toString()}
                            value={receiver.id.toString()}
                          >
                            {receiver.name}
                          </SelectItem>
                        ))}
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
